"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterX, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { Navigate } from "react-router-dom";

import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { Button } from "@/components/ui-system/button";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSearchInputClass, ThemedSelect } from "@/components/ui-system/select";
import { createTenant, deleteTenant, deleteTenantLogo, listTenants, type TenantMembershipDto, updateTenant, updateTenantOwner, uploadTenantLogo } from "@/lib/api/endpoints/tenants";
import { env } from "@/lib/env";
import { listUsers } from "@/lib/api/endpoints/users";
import { isAppHttpError } from "@/lib/api/client";
import type { UserDto } from "@/lib/api/types/enterprise";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { isSuperAdmin } from "@/modules/auth/permissions";
import { useAuthStore } from "@/modules/auth/store";

type TenantFormState = {
  name: string;
  company_name: string;
  domain: string;
  owner_id: string;
  is_active: boolean;
  logo_file: File | null;
  remove_logo: boolean;
  current_logo_url: string | null;
};

const EMPTY_FORM: TenantFormState = {
  name: "",
  company_name: "",
  domain: "",
  owner_id: "",
  is_active: true,
  logo_file: null,
  remove_logo: false,
  current_logo_url: null,
};
const TENANT_PAGE_SIZE_OPTIONS = [10, 25, 50];

function errorDetail(err: unknown): string {
  if (isAppHttpError(err)) return err.message;
  if (err instanceof Error) return err.message;
  return "Request failed";
}

function userBelongsToWorkspace(user: UserDto, workspaceId: string): boolean {
  if (user.tenant_id === workspaceId) return true;
  if (user.tenant_ids?.includes(workspaceId)) return true;
  return user.tenants?.some((tenant) => tenant.id === workspaceId) ?? false;
}

function ownerLabel(user: UserDto | undefined): string {
  if (!user) return "Unassigned";
  return user.name ? `${user.name} (${user.email})` : user.email;
}

export function TenantsPage() {
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canCreateTenants = hasModulePermission(user, permissionMap, "tenants", "create");
  const canEditTenants = hasModulePermission(user, permissionMap, "tenants", "edit");
  const canDeleteTenants = hasModulePermission(user, permissionMap, "tenants", "delete");
  const queryClient = useQueryClient();
  const dialogs = useAppDialogs();
  const [form, setForm] = useState<TenantFormState>(EMPTY_FORM);
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(25);

  const tenantsQuery = useQuery({
    queryKey: ["tenants-management"],
    queryFn: listTenants,
    enabled: isSuperAdmin(user),
    staleTime: 60_000,
  });
  const usersQuery = useQuery({
    queryKey: ["users", "all", "workspace-owner-options"],
    queryFn: () => listUsers(null, "all"),
    enabled: isSuperAdmin(user),
    staleTime: 60_000,
  });

  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);
  const allUsers = usersQuery.data?.items ?? [];
  const filteredTenants = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tenants;
    return tenants.filter((tenant) =>
      [
        tenant.name,
        tenant.company_name ?? "",
        tenant.domain ?? "",
        ownerLabel(allUsers.find((candidate) => candidate.id === tenant.owner_id)),
        tenant.is_active ? "active" : "inactive",
      ]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [allUsers, search, tenants]);
  const pageCount = Math.max(1, Math.ceil(filteredTenants.length / pageSize));
  const pageTenants = useMemo(
    () => filteredTenants.slice(page * pageSize, page * pageSize + pageSize),
    [filteredTenants, page, pageSize],
  );
  const pageStart = filteredTenants.length === 0 ? 0 : page * pageSize + 1;
  const pageEnd = filteredTenants.length === 0 ? 0 : page * pageSize + pageTenants.length;
  const ownerOptions = useMemo(
    () =>
      editingTenantId
        ? allUsers.filter(
            (candidate) =>
              candidate.role !== "super_admin" && userBelongsToWorkspace(candidate, editingTenantId),
          )
        : allUsers.filter((candidate) => candidate.role !== "super_admin"),
    [allUsers, editingTenantId],
  );

  useEffect(() => {
    setPage(0);
  }, [search, pageSize]);

  useEffect(() => {
    if (page >= pageCount) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingTenantId(null);
    setError(null);
  };

  const openCreate = () => {
    if (!canCreateTenants) {
      setToast("You don't have permission to create workspaces.");
      return;
    }
    resetForm();
    setShowModal(true);
  };

  const openEdit = (tenant: { id: string; name: string; company_name?: string | null; domain: string | null; owner_id?: string | null; logo_url?: string | null; is_active: boolean }) => {
    if (!canEditTenants) {
      setToast("You don't have permission to edit workspaces.");
      return;
    }
    setEditingTenantId(tenant.id);
    setForm({
      name: tenant.name,
      company_name: tenant.company_name ?? tenant.name,
      domain: tenant.domain ?? "",
      owner_id: tenant.owner_id ?? "",
      is_active: tenant.is_active,
      logo_file: null,
      remove_logo: false,
      current_logo_url: tenant.logo_url ?? null,
    });
    setError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const upsertMutation = useMutation({
    mutationFn: async () => {
      let tenant: TenantMembershipDto;
      if (editingTenantId) {
        tenant = await updateTenant(editingTenantId, {
          name: form.name,
          company_name: form.company_name.trim() || form.name.trim(),
          domain: form.domain.trim() ? form.domain.trim() : null,
          is_active: form.is_active,
        });
        if (form.owner_id && form.owner_id !== tenant.owner_id) {
          tenant = await updateTenantOwner(editingTenantId, form.owner_id);
        }
      } else {
        tenant = await createTenant({
          name: form.name,
          company_name: form.company_name.trim() || form.name.trim(),
          domain: form.domain.trim() ? form.domain.trim() : null,
          owner_id: form.owner_id,
          is_active: form.is_active,
        });
      }
      if (form.logo_file) {
        tenant = await uploadTenantLogo(tenant.id, form.logo_file);
      } else if (editingTenantId && form.remove_logo && form.current_logo_url) {
        tenant = await deleteTenantLogo(tenant.id);
      }
      return tenant;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tenants-management"] }),
        queryClient.invalidateQueries({ queryKey: ["tenant-memberships"] }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
      ]);
      closeModal();
    },
    onError: (err) => setError(errorDetail(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (tenantId: string) => deleteTenant(tenantId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tenants-management"] }),
        queryClient.invalidateQueries({ queryKey: ["tenant-memberships"] }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
      ]);
      closeModal();
    },
    onError: (err) => setError(errorDetail(err)),
  });

  if (!isSuperAdmin(user)) {
    return <Navigate to="/overview" replace />;
  }

  return (
    <EnterprisePageShell
      hideHeader
      title="Workspaces"
      description="Create and manage top-level workspaces for users, dashboards, devices, and organizations."
    >
      {showModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/55 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
        >
          <div
            className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg min-w-0 flex-col overflow-hidden rounded-card border border-card-border bg-surface-elevated shadow-overlay"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-card-border bg-surface-secondary px-5 py-4">
              <h2 className="text-base font-semibold text-foreground">
                {editingTenantId ? "Edit workspace" : "Create workspace"}
              </h2>
              <p className="mt-1 text-sm text-muted">
                {editingTenantId
                  ? "Update workspace details and status."
                  : "Create a new workspace for users, dashboards, devices, and organizations."}
              </p>
            </div>

            <form
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              onSubmit={(event) => {
                event.preventDefault();
                setError(null);
                upsertMutation.mutate();
              }}
            >
              <div className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-5 py-5">
                <div className="grid min-w-0 gap-4">
              <label className="grid gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">Name</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="ui-control h-11 rounded-control bg-surface-primary px-3 text-caption"
                  placeholder="Atech Demo Workspace"
                  required
                />
              </label>

              <label className="grid gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">Company name</span>
                <input
                  value={form.company_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, company_name: event.target.value }))}
                  className="ui-control h-11 rounded-control bg-surface-primary px-3 text-caption"
                  placeholder="Atech Energy"
                />
                <span className="text-xs text-muted">Shown as the customer identity in the sidebar for workspace members.</span>
              </label>

              <div className="grid gap-2 rounded-xl border border-card-border bg-surface-secondary p-3">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">Workspace logo</span>
                  <p className="mt-1 text-xs text-muted">Optional. Recommended: 256 × 256 px square, PNG, JPG, or WebP, up to 2 MB.</p>
                </div>
                {form.current_logo_url && !form.remove_logo && !form.logo_file ? (
                  <div className="flex items-center gap-3">
                    <img src={`${env.publicApiUrl}${form.current_logo_url}`} alt="Current workspace logo" className="h-12 w-12 rounded-control border border-card-border bg-surface-primary object-contain p-1" />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm((prev) => ({ ...prev, remove_logo: true }))}>Remove logo</button>
                  </div>
                ) : null}
                {form.logo_file ? (
                  <div className="flex min-w-0 items-center gap-3 text-sm text-foreground">
                    <span className="min-w-0 flex-1 truncate">{form.logo_file.name}</span>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm((prev) => ({ ...prev, logo_file: null }))}>Remove</button>
                  </div>
                ) : (
                  <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-control border border-card-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-surface-hover">
                    <Upload className="h-4 w-4" />
                    {form.current_logo_url && !form.remove_logo ? "Replace logo" : "Upload logo"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        if (file) setForm((prev) => ({ ...prev, logo_file: file, remove_logo: false }));
                        event.currentTarget.value = "";
                      }}
                    />
                  </label>
                )}
                {form.remove_logo ? <p className="text-xs text-warning">The current logo will be removed when you save.</p> : null}
              </div>

              <label className="grid gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">Domain</span>
                <input
                  value={form.domain}
                  onChange={(event) => setForm((prev) => ({ ...prev, domain: event.target.value }))}
                  className="ui-control h-11 rounded-control bg-surface-primary px-3 text-caption"
                  placeholder="demo.atech.local"
                />
              </label>

              <label className="flex items-center gap-3 rounded-xl border border-card-border bg-card px-3 py-3">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                  className="h-4 w-4 rounded border-[#d1d5db] text-[var(--btn-primary-to)] focus:ring-[var(--btn-primary-to)]/20"
                />
                <span className="text-sm text-foreground">Workspace is active</span>
              </label>

              <label className="grid gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">Owner</span>
                <select
                  value={form.owner_id}
                  onChange={(event) => setForm((prev) => ({ ...prev, owner_id: event.target.value }))}
              className="ui-control h-11 rounded-control bg-surface-primary px-3 text-caption"
                  required={!editingTenantId}
                >
                  <option value="">Select owner</option>
                  {ownerOptions.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {ownerLabel(candidate)}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-muted">
                  {editingTenantId
                    ? "Owner must already belong to this workspace and cannot be a super admin."
                    : "The owner will be added to the new workspace and cannot be a super admin."}
                </span>
              </label>

              {error ? (
                <p className="rounded-control border border-error/30 bg-error/10 px-3 py-2 text-caption text-error" role="alert">
                  {error}
                </p>
              ) : null}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-card-border bg-surface-elevated px-5 py-4">
                <Button type="button" variant="secondary" size="md" onClick={closeModal} className="h-11 px-4">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={upsertMutation.isPending || (!editingTenantId && !form.owner_id)}
                  className="h-11 px-4"
                >
                  {upsertMutation.isPending ? "Saving..." : editingTenantId ? "Update workspace" : "Create workspace"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <ListSurface>
        <ListSurfaceHeader
          title="Workspace list"
          meta={
            <ListCount
              total={tenants.length}
              filtered={filteredTenants.length}
              noun={filteredTenants.length === 1 ? "workspace" : "workspaces"}
            />
          }
          controls={
            <ListToolbar>
              <ListToolbarGroup grow>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search workspaces"
                  className={tableHeaderSearchInputClass}
                />
              </ListToolbarGroup>
              {search.trim() ? (
                <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} />
              ) : null}
              {canCreateTenants ? (
                <ToolbarIconButton icon={Plus} label="New workspace" variant="primary" onClick={openCreate} />
              ) : null}
            </ListToolbar>
          }
        />
        <ListSurfaceBody className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[color:var(--table-border)] text-sm">
            <thead className="table-header-row bg-[var(--table-header-bg)] text-left text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Domain</th>
                <th className="px-5 py-3">Owner</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--table-border)]">
              {pageTenants.length > 0 ? (
                pageTenants.map((tenant, index) => (
                  <tr key={tenant.id} className="table-data-row align-top hover:bg-[var(--table-row-hover)]" style={{ background: index % 2 === 1 ? "color-mix(in srgb, var(--muted-bg) 38%, transparent)" : "var(--bg-surface)" }}>
                    <td className="px-5 py-4 font-medium text-foreground">
                      <div className="flex min-w-0 items-center gap-3">
                        {tenant.logo_url ? (
                          <img
                            src={`${env.publicApiUrl}${tenant.logo_url}`}
                            alt=""
                            className="h-9 w-9 shrink-0 rounded-control border border-card-border bg-surface-primary object-contain p-1"
                          />
                        ) : null}
                        <span className="truncate">{tenant.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-muted">{tenant.domain ?? "—"}</td>
                    <td className="px-5 py-4 text-muted">
                      {ownerLabel(allUsers.find((candidate) => candidate.id === tenant.owner_id))}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          tenant.is_active
                            ? "bg-success/15 text-success"
                            : "bg-muted-bg text-muted"
                        }`}
                      >
                        {tenant.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-muted">
                      {tenant.created_at ? new Date(tenant.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(tenant)}
                          title={`Edit ${tenant.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-primary transition-colors hover:border-primary/25 hover:bg-primary/15"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!canDeleteTenants) {
                              setToast("You don't have permission to delete workspaces.");
                              return;
                            }
                            const confirmed = await dialogs.confirm({
                              title: `Delete workspace "${tenant.name}"?`,
                              description: "This removes the workspace from the platform immediately.",
                              confirmLabel: "Delete workspace",
                              tone: "danger",
                            });
                            if (!confirmed) {
                              return;
                            }
                            deleteMutation.mutate(tenant.id);
                          }}
                          title={`Delete ${tenant.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-error/25 bg-error/10 text-error transition-colors hover:bg-error/15"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : tenantsQuery.isLoading ? (
                <tr>
                  <td className="px-5 py-4" colSpan={6}>
                    {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
                  </td>
                </tr>
              ) : (
                <tr>
                  <td className="px-5 py-8 text-muted" colSpan={6}>
                    {search.trim() ? "No workspaces match the current filters." : "No workspaces found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ListSurfaceBody>
        <ListSurfaceFooter
          left={
            <label className="flex items-center gap-ds-2">
              <span className="text-caption text-muted">Rows per page</span>
              <ThemedSelect
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                ariaLabel="Rows per page"
                className="w-[5.5rem]"
                triggerClassName="h-10 rounded-control border-card-border bg-surface-primary hover:border-primary/20"
                options={TENANT_PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
              />
            </label>
          }
          right={
            <>
              <span className="text-caption text-muted">
                {filteredTenants.length === 0 ? "No pages" : `Showing ${pageStart}-${pageEnd} of ${filteredTenants.length}`}
              </span>
              <div className="flex items-center gap-ds-2">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  Previous
                </button>
                <span className="text-caption tabular-nums text-muted">
                  Page {page + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page + 1 >= pageCount}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </button>
              </div>
            </>
          }
        />
      </ListSurface>
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </EnterprisePageShell>
  );
}
